import { useEffect, useState } from "react";
import { nativeGuideAvailable, nativeGuideProgress, type GuideProgress } from "../api/nativeGuide.js";

/** How often the line asks the device how far the download has got. */
const POLL_MS = 1_000;

const mb = (bytes: number) => Math.round(bytes / 1_048_576);

/**
 * "Loading the guide… 42 MB of 106 MB" while the device downloads and reads the
 * provider's guide (roadmap: first-run guide speed). A first install on the onn box
 * took 161 s for a 106.5 MB feed with nothing on screen to say so (2026-10-11), and
 * the projector longer still. Shown only while a download runs; not a stop for the
 * remote, so the guide's navigation is unchanged.
 */
export function GuideLoadingStatus() {
  const [progress, setProgress] = useState<GuideProgress | null>(null);
  useEffect(() => {
    if (!nativeGuideAvailable()) return;
    let stopped = false;
    const ask = () => {
      nativeGuideProgress()
        .then((next) => {
          if (!stopped) setProgress(next);
        })
        .catch(() => {
          // An older APK without the progress call: no line, as before.
          stopped = true;
        });
    };
    ask();
    const timer = window.setInterval(() => {
      if (!stopped) ask();
    }, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, []);
  if (!progress?.downloading) return null;
  const known = progress.total > 0;
  const percent = known ? Math.min(99, Math.floor((progress.bytes / progress.total) * 100)) : null;
  return (
    <div className="guide-loading" role="status" aria-live="polite">
      <span>
        Loading the guide… {mb(progress.bytes)} MB{known ? ` of ${mb(progress.total)} MB` : ""}
        {percent !== null ? ` (${percent}%)` : ""}
      </span>
      {percent !== null && (
        <span className="guide-loading__bar" aria-hidden>
          <span className="guide-loading__fill" style={{ width: `${percent}%` }} />
        </span>
      )}
    </div>
  );
}
